import videojs from 'video.js'
import { registerPluginOnce } from '../utils'

const Plugin = videojs.getPlugin('plugin')

// Default options for the plugin.
const defaults = {
  name: 'default',
  accentColor: null,
  buttonOrders: []
}

/**
 * An advanced Video.js plugin. For more information on the API
 *
 * See: https://blog.videojs.com/feature-spotlight-advanced-plugins/
 */
class NFBTheme extends Plugin {
  /**
   * Create a NFBTheme plugin instance.
   *
   * @param  {Player} player
   *         A Video.js Player instance.
   *
   * @param  {Object} [options]
   *         An optional options object.
   *
   *         While not a core part of the Video.js plugin architecture, a
   *         second argument of options is a convenient way to accept inputs
   *         from your plugin's caller.
   */
  constructor (player, options) {
    // the parent class will add player under this.player
    super(player)
    this.log.level(videojs.log.level())

    this.options = videojs.mergeOptions(defaults, options)
    this.domEls = {}
    this.playerEl = this.player.el()
    this.init = this.init.bind(this)
    this.onLoaded = this.onLoaded.bind(this)

    this.player.ready(this.init)
    this.player.on('loadeddata', this.onLoaded)
  }

  init () {
    this.log('Initializing NFB Theme plugin')
    this.player.addClass(`vjs-nfb-theme-${this.options.name}`)
    import(`../sass/themes/${this.options.name}/index.sass`)
    if (videojs.browser.IS_SAFARI) {
      this.player.addClass('is-safari')
    }

    switch (this.options.name) {
    case 'compact':
      this.initCompact()
      break
    default:
      this.initDefault()
    }
  }

  onLoaded () {
    if (this.options.buttonOrders.length > 0) {
      this.updateButtonOrder()
    }
    this.addTitleLegendToQualitySelector()
  }

  initDefault () {
    this.initColors()
    this.updateDOM()
    this.updateDefaultDOM()
  }

  initCompact () {
    this.updateDOM()
  }

  initColors () {
    if (this.options.accentColor) {
      this.log('Update Accent Color')
      if (/(^#[a-zA-Z0-9]{3}$|^#[a-zA-Z0-9]{6}$)/.test(this.options.accentColor)) {
        document.documentElement.style.setProperty('--accent-color', this.options.accentColor)
      } else {
        this.player.addClass(`accent-${this.options.accentColor}`)
      }
    }
  }

  updateDOM () {
    this.log('Update DOM')
    this.domEls = {
      controlBar: this.playerEl.querySelector('.vjs-control-bar'),
      progressControl: this.playerEl.querySelector('.vjs-progress-control')
    }
  }

  updateDefaultDOM () {
    this.log('Update DOM')
    this.domEls.progressControlContainer = document.createElement('div')
    this.domEls.progressControlContainer.classList.add('vjs-progress-control-container')
    this.domEls.controlBar.prepend(this.domEls.progressControlContainer)
    this.domEls.progressControlContainer.appendChild(this.domEls.progressControl)
  }

  updateButtonOrder () {
    this.log('Update Button Order')
    const container = document.createElement('div')
    container.className = 'vjs-control-custom-container'
    this.options.buttonOrders.forEach(selector => {
      const el = this.playerEl.querySelector(selector)
      if (el) {
        container.appendChild(el)
      }
    })
    this.domEls.controlBar.appendChild(container)
  }

  addTitleLegendToQualitySelector () {
    this.log('Add title legend to quality selector button')
    if (this.player.hasPlugin('hlsQualitySelector')) {
      const qualitySelectorButton = this.playerEl.querySelector('.vjs-quality-selector.vjs-control button')
      if (qualitySelectorButton) {
        qualitySelectorButton.setAttribute('title', this.player.localize('Quality Selector'))
      }
    }
  }
}

// Define default values for the plugin's `state` object here.
NFBTheme.defaultState = {}

// Include the version number.
NFBTheme.VERSION = '1.0.0'

// Register the plugin
registerPluginOnce(videojs, 'nfbTheme', NFBTheme)

export default NFBTheme
